/* eslint-disable react/prop-types */
import React, { useCallback, useMemo, useState, memo } from "react";
import cn from "classnames";
import { useSetAtom } from "jotai";
import { isExportPdfViewOpenAtom } from "frontend/app-header/left-header/atoms/left-header-atoms";
import { ExportButton } from "frontend/app-header/left-header/views/export/export-button";
import { useHeaderContext } from "frontend/context/header-context";
import Modal from "frontend/modal/modal";
import ModalContainer from "frontend/modals/modal-container";
import { useBoardValue } from "frontend/state/board-state";
import { Box } from "frontend/ui-components/design-system/box/box";
import Checkbox from "frontend/ui-components/design-system/checkbox/checkbox";
import DropdownPicker from "frontend/ui-components/picker/dropdown-picker";
import { RadioButton, RadioButtonGroup } from "frontend/ui-components/radio-button/radio-button";
import { EventFor } from "utils/fn-utils";
import { minmax } from "frontend/geometry/utils";
import { getPathPrefix } from "utils/getPathPrefix";
import styles from "./export-view.module.css";
import { ChevronDown } from "frontend/icons/chevron";

// Type definitions
interface Frame {
  id: string;
  name: string;
}

interface ExportConfig {
  format: "pdf";
  quality: "high";
  whiteBackground: boolean;
  singlePage: boolean;
  frames: Frame[];
}

// Constants
const EXPORT_CONFIG_BASE = {
  format: "pdf" as const,
  quality: "high" as const,
  whiteBackground: true,
};

// Memoized sub-component for better performance
const FrameOption = memo<{
  frame: Frame;
  selected: boolean;
  index: number;
}>(({ frame, selected, index }) => (
  <div className={styles.frameItem}>
    <Checkbox checked={selected} onChange={() => {}} />
    <div>{index === 0 ? "All frames" : `${index.toString().padStart(2, "0")}. ${frame.name}`}</div>
  </div>
));
FrameOption.displayName = "FrameOption";

interface FramesPickerProps {
  frames: Frame[];
  onChange: (frames: Frame[]) => void;
  disabled?: boolean;
}

const FramesPicker: React.FC<FramesPickerProps> = memo(({ frames, onChange, disabled = false }) => {
  // Initialize with all frames selected
  const [selectedFrames, setSelectedFrames] = useState<Frame[]>(frames);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const hasInitialized = React.useRef(false);

  // Notify parent of initial selection only once
  React.useEffect(() => {
    if (!hasInitialized.current && frames.length > 0) {
      onChange(frames);
      hasInitialized.current = true;
    }
  }, [frames, onChange]);

  // Memoize selectable frames
  const selectableFrames = useMemo(() => frames.filter((f) => f.id !== "all"), [frames]);

  const setSelectedIds = useCallback(
    (newSelectedIds: Frame[], updateInput: boolean) => {
      setSelectedFrames(newSelectedIds);
      onChange(newSelectedIds);
      if (updateInput && inputRef.current) {
        inputRef.current.value = selectionToText(newSelectedIds);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange]
  );

  // Optimized selection to text conversion
  const selectionToText = useCallback(
    (selection?: Frame[]) => {
      const ids = (selection ?? selectedFrames).map((f) => f.id).filter((id) => id !== "all");

      if (ids.length === selectableFrames.length && ids.length > 0) {
        return "All frames";
      }

      const indices = ids
        .map((id) => selectableFrames.findIndex((f) => f.id === id) + 1)
        .filter((index) => index > 0)
        .sort((a, b) => a - b);

      if (indices.length === 0) return "";

      // Convert indices to ranges
      const ranges: string[] = [];
      let start = indices[0];
      let end = start;

      for (let idx = 1; idx <= indices.length; idx++) {
        if (idx < indices.length && indices[idx] === end + 1) {
          end = indices[idx];
        } else {
          ranges.push(start === end ? `${start}` : `${start}-${end}`);
          if (idx < indices.length) {
            start = end = indices[idx];
          }
        }
      }

      return ranges.join(", ");
    },
    [selectedFrames, selectableFrames]
  );

  const onFrameChange = useCallback(
    (frame: Frame) => {
      if (frame.id === "all") {
        const shouldSelectAll = selectedFrames.length !== frames.length;
        const newSelection = shouldSelectAll ? frames : [];
        setSelectedIds(newSelection, true);
        return;
      }

      // Toggle individual frame selection
      const isSelected = selectedFrames.some((f) => f.id === frame.id);
      let newSelection = isSelected ? selectedFrames.filter((f) => f.id !== frame.id) : [...selectedFrames, frame];

      // Auto-manage "all" option
      const allSelected = selectableFrames.every((f) => newSelection.some((selected) => selected.id === f.id));

      if (allSelected && !newSelection.some((f) => f.id === "all")) {
        const allFrame = frames.find((f) => f.id === "all");
        if (allFrame) {
          newSelection = [...newSelection, allFrame];
        }
      } else if (!allSelected) {
        newSelection = newSelection.filter((f) => f.id !== "all");
      }

      setSelectedIds(newSelection, true);
    },
    [selectedFrames, frames, selectableFrames, setSelectedIds]
  );

  // Optimized text parsing with early returns
  const parseText = useCallback(
    (event: EventFor<"input", "onChange">) => {
      const input = event.currentTarget.value.trim();
      if (!input) {
        setSelectedIds([], false);
        return;
      }

      const terms = input.split(/[,\s]+/).filter(Boolean);
      const frameIndices = new Set<number>();

      for (const term of terms) {
        if (/^\d+$/.test(term)) {
          frameIndices.add(Number(term));
        } else if (/^\d+-\d+$/.test(term)) {
          let [start, end] = term.split("-").map(Number);
          [start, end] = minmax(start, end);
          start = Math.max(1, start);
          end = Math.min(selectableFrames.length, end);

          for (let idx = start; idx <= end; idx++) {
            frameIndices.add(idx);
          }
        } else if (/all/i.test(term)) {
          const hasAllOption = frames.some((f) => f.id === "all");
          const allFrames = hasAllOption ? frames : frames.filter((f) => f.id !== "all");
          setSelectedIds(allFrames, false);
          return;
        }
      }

      const selectedIds = selectableFrames.filter((_, index) => frameIndices.has(index + 1));

      // Add "all" if all frames selected
      const allFrame = frames.find((f) => f.id === "all");
      const finalSelection =
        selectedIds.length === selectableFrames.length && selectedIds.length > 0 && allFrame
          ? [allFrame, ...selectedIds]
          : selectedIds;

      setSelectedIds(finalSelection, false);
    },
    [frames, selectableFrames, setSelectedIds]
  );

  // Memoize dropdown configuration
  const dropdownConfig = useMemo(
    () => ({
      pickerCustomIcon: getPathPrefix("/images/chevron-black.svg"),
      pickerCustomStyle: {
        borderRadius: "6px",
        opacity: disabled ? 0.5 : 1,
        width: "181px",
        height: "38px",
      },
      optionsPickerStyle: {
        fontSize: 14,
        maxWidth: 250,
        width: "180px",
        textOverflow: "ellipsis" as const,
        whiteSpace: "nowrap" as const,
        overflow: "hidden" as const,
        maxHeight: "200px",
      },
      optionsContainerStyle: {
        fontSize: 16,
        color: "#113357",
        borderRadius: "6px",
        minWidth: "100%",
        width: "100%",
      },
    }),
    [disabled]
  );

  return (
    <DropdownPicker
      options={frames}
      multiValue={true}
      titles={(frame: Frame) => frame.name}
      onChange={onFrameChange}
      isSelected={(frame: Frame) => selectedFrames.some((f) => f.id === frame.id)}
      enabled={!disabled}
      scrollToSelected={true}
      {...dropdownConfig}
      styles={{
        optionClassName: styles.selectedOption,
        rounded: true,
        pickerParent: styles.dropdownPicker,
        optionListClassName: styles.optionsList,
      }}
      selectionHeaderRenderer={() => (
        <div className={styles.selectedTitle}>
          <input
            ref={inputRef}
            type="text"
            id="frames-header-input"
            onChange={parseText}
            onBlur={() => inputRef.current && (inputRef.current.value = selectionToText())}
            defaultValue={selectionToText()}
            disabled={disabled}
            className={styles.framesHeaderInput}
          />
        </div>
      )}
      optionRenderer={({ option, selected, index }) => ({
        element: <FrameOption frame={option} selected={selected} index={index} />,
        enabled: true,
        handlesSelectedState: true,
      })}
      iconRenderer={() => <ChevronDown />}
    />
  );
});

FramesPicker.displayName = "FramesPicker";

// Header component extracted for better organization
const ExportHeader = memo(() => (
  <div>
    <div className={cn(styles.title, styles.pdfTitle)}>Export as PDF</div>
    <div className={styles.subtitle}>What to export?</div>
  </div>
));
ExportHeader.displayName = "ExportHeader";

export const ExportPdfView = memo(() => {
  const setIsExportPdfViewOpen = useSetAtom(isExportPdfViewOpenAtom);
  const [exportType, setExportType] = useState<"all" | "specific">("all");
  const [{ stageRef }] = useBoardValue();
  const [selectedFrames, setSelectedFrames] = useState<Frame[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  // Memoize frames retrieval
  const frames = useMemo(() => stageRef?.getVisibleFramesByOrder?.() ?? [], [stageRef]);

  // Handlers
  const handleCloseModal = useCallback(() => {
    setIsExportPdfViewOpen(false);
  }, [setIsExportPdfViewOpen]);

  const handleExportTypeChange = useCallback((value: string) => {
    setExportType(value as "all" | "specific");
  }, []);

  // Memoize frame options with "all" option
  const framesOptions = useMemo(
    () => [
      { id: "all", name: "Entire canvas" },
      ...frames.map((frame) => ({
        id: frame.id,
        name: frame.name,
      })),
    ],
    [frames]
  );

  const { onExport } = useHeaderContext();

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      // Determine frames to export
      const framesToExport =
        exportType === "all"
          ? framesOptions.filter((f) => f.id !== "all")
          : selectedFrames.length === 0
          ? framesOptions.filter((f) => f.id !== "all")
          : selectedFrames.filter((f) => f.id !== "all");

      // Export with configuration
      await onExport(false, {
        ...EXPORT_CONFIG_BASE,
        singlePage: exportType === "all",
        frames: framesToExport,
      } as ExportConfig);

      handleCloseModal();
    } catch (error) {
      console.error("Export failed:", error);
      // Consider showing user-friendly error message
    } finally {
      setIsExporting(false);
    }
  }, [exportType, selectedFrames, framesOptions, onExport, handleCloseModal]);

  // Validation
  const isExportDisabled = useMemo(
    () => exportType === "specific" && selectedFrames.filter((f) => f.id !== "all").length === 0,
    [exportType, selectedFrames]
  );

  return (
    <Modal dimBackground onDismissOnEscape={handleCloseModal} onClickBackground={handleCloseModal} theme="cim-dark">
      <ModalContainer onDismiss={handleCloseModal} showCloseButton>
        <Box className={cn(styles.baseModal, styles.pdfContent)}>
          <ExportHeader />

          <RadioButtonGroup
            name="export-pdf"
            onChange={handleExportTypeChange}
            classes={{ group: { marginBottom: "34px" } }}
          >
            <RadioButton value="all" label="Entire canvas" initialChecked={true} />
            <RadioButton
              value="specific"
              label={
                <div className={styles.specificFrames}>
                  Specific frames
                  <FramesPicker
                    frames={framesOptions}
                    onChange={setSelectedFrames}
                    disabled={exportType !== "specific"}
                  />
                </div>
              }
            />
          </RadioButtonGroup>

          <ExportButton onClick={handleExport} isExporting={isExporting} disabled={isExportDisabled} />
        </Box>
      </ModalContainer>
    </Modal>
  );
});

ExportPdfView.displayName = "ExportPdfView";
